/**
 * Utility functions for calculating node connections
 */
import type { Node, Edge } from '@xyflow/react'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'

export interface ConnectionData {
  [handle: string]: string | null
}

function initConnectionsFromSchema(nodeType: string | undefined): {
  connections: ConnectionData
  firstInputId: string | null
} {
  const connections: ConnectionData = {}
  if (!nodeType) return { connections, firstInputId: null }

  const schema = UniversalNodeRegistry.getSchemaResolved(nodeType)
  if (!schema) return { connections, firstInputId: null }

  for (const input of schema.inputs) connections[input.id] = null
  return { connections, firstInputId: schema.inputs[0]?.id ?? null }
}

/**
 * Pre-computed edge adjacency maps for O(1) lookups.
 * Replaces O(E) filter + O(N) find per node with O(1) map reads.
 */
export interface EdgeAdjacency {
  /** Edges grouped by target node ID */
  byTarget: Map<string, Edge[]>
  /** Edges grouped by source node ID */
  bySource: Map<string, Edge[]>
  /** Node type lookup by node ID */
  nodeTypeById: Map<string, string>
  /** Full node lookup by node ID */
  nodeById: Map<string, Node>
}

/**
 * Build edge adjacency maps in O(N+E) for fast lookups.
 * Call once per edge/node topology change, then use for all per-node calculations.
 */
export function buildEdgeAdjacency(nodes: Node[], edges: Edge[]): EdgeAdjacency {
  const byTarget = new Map<string, Edge[]>()
  const bySource = new Map<string, Edge[]>()
  const nodeTypeById = new Map<string, string>()
  const nodeById = new Map<string, Node>()

  for (const node of nodes) {
    nodeTypeById.set(node.id, node.type || '')
    nodeById.set(node.id, node)
  }

  for (const edge of edges) {
    let targetList = byTarget.get(edge.target)
    if (!targetList) {
      targetList = []
      byTarget.set(edge.target, targetList)
    }
    targetList.push(edge)

    let sourceList = bySource.get(edge.source)
    if (!sourceList) {
      sourceList = []
      bySource.set(edge.source, sourceList)
    }
    sourceList.push(edge)
  }

  return { byTarget, bySource, nodeTypeById, nodeById }
}

/**
 * Fast connection calculation using pre-computed adjacency map.
 * O(degree) per node instead of O(N+E).
 */
export function calculateNodeConnectionsFast(
  nodeId: string,
  adjacency: EdgeAdjacency,
): ConnectionData {
  const incomingEdges = adjacency.byTarget.get(nodeId) || []
  const targetType = adjacency.nodeTypeById.get(nodeId)
  const { connections, firstInputId } = initConnectionsFromSchema(targetType)

  for (const edge of incomingEdges) {
    // Resolve group I/O pass-throughs to the REAL upstream source — the
    // connection UI should show "Price Data", not "subnetInput".
    let sourceId = edge.source
    let sourceType = adjacency.nodeTypeById.get(sourceId)
    const seen = new Set<string>()
    while (sourceType && isPassthroughType(sourceType) && !seen.has(sourceId)) {
      seen.add(sourceId)
      const upstream = (adjacency.byTarget.get(sourceId) || [])[0]
      if (!upstream) {
        sourceType = undefined
        break
      }
      sourceId = upstream.source
      sourceType = adjacency.nodeTypeById.get(sourceId)
    }
    if (!sourceType) continue

    const handle = edge.targetHandle
    if (handle) {
      connections[handle] = sourceType
    } else if (firstInputId) {
      connections[firstInputId] = sourceType
    }
  }

  return connections
}

/**
 * Calculate connection data for a node based on its incoming edges
 * @param nodeId - The ID of the target node
 * @param nodes - Array of all nodes
 * @param edges - Array of all edges
 * @returns Object mapping handle names to connected source node types
 */
export function calculateNodeConnections(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
): ConnectionData {
  const incomingEdges = edges.filter((edge) => edge.target === nodeId)
  const targetNode = nodes.find((n) => n.id === nodeId)
  const { connections, firstInputId } = initConnectionsFromSchema(targetNode?.type)

  for (const edge of incomingEdges) {
    // Resolve group I/O pass-throughs to the REAL upstream source (see
    // calculateNodeConnectionsFast).
    let sourceNode = nodes.find((node) => node.id === edge.source)
    const seen = new Set<string>()
    while (sourceNode?.type && isPassthroughType(sourceNode.type) && !seen.has(sourceNode.id)) {
      seen.add(sourceNode.id)
      const currentId = sourceNode.id
      const upstream = edges.find((e) => e.target === currentId)
      sourceNode = upstream ? nodes.find((node) => node.id === upstream.source) : undefined
    }
    if (!sourceNode?.type) continue

    const handle = edge.targetHandle
    if (handle) {
      connections[handle] = sourceNode.type
    } else if (firstInputId) {
      connections[firstInputId] = sourceNode.type
    }
  }

  return connections
}
